import { Component, OnChanges, OnInit, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { WorkflowActionCatalogApiItem, WorkflowOutgoingFlowOption } from '../workflow-catalog.models';
import { WorkflowApiService } from '../workflow-api.service';
import {
  WorkflowInteractiveOperatorActionConfig,
  WorkflowOutgoingFlowBinding
} from '../../../shared/workflow-actions/workflow-action-catalog.types';
import { parseConfigJson, stringifyConfigJson } from './workflow-operator-config.utils';

@Component({
  standalone: true,
  selector: 'ain-workflow-action-routing-panel',
  imports: [FormsModule, SelectModule, ButtonModule],
  template: `
    <section class="operator-panel">
      <h5>{{ t('workflow.designer.operators.actions.title') }}</h5>
      <p class="hint">{{ t('workflow.designer.operators.actions.description') }}</p>

      @for (row of rows; track row.actionKey) {
        <div class="action-row">
          <div class="action-label">
            <strong>{{ actionLabel(row.actionKey) }}</strong>
            <small>{{ row.variableKey }} = {{ row.variableValue }}</small>
          </div>

          <p-select
            [options]="flowOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="row.flowId"
            (ngModelChange)="onBindingChanged(row)"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('workflow.designer.operators.actions.selectFlow')" />

          @if (row.suggestedConditionExpression && row.flowId) {
            <div class="condition-row">
              <code>{{ row.suggestedConditionExpression }}</code>
              <button
                pButton
                type="button"
                size="small"
                severity="secondary"
                [label]="t('workflow.designer.operators.actions.applyCondition')"
                (click)="applyCondition(row)"></button>
            </div>
          }
        </div>
      }
    </section>
  `,
  styles: [`
    .operator-panel { display: flex; flex-direction: column; gap: 10px; margin-top: 8px; }
    .operator-panel h5 { margin: 8px 0 4px; font-size: 13px; color: #334155; }
    .hint { font-size: 12px; color: #64748b; margin: 0 0 4px; }
    .action-row { display: flex; flex-direction: column; gap: 6px; padding: 8px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
    .action-label { display: flex; flex-direction: column; gap: 2px; }
    .action-label small { color: #64748b; font-size: 11px; }
    .condition-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .condition-row code { font-size: 11px; background: #f1f5f9; padding: 4px 6px; border-radius: 4px; }
  `]
})
export class WorkflowActionRoutingPanelComponent implements OnInit, OnChanges {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);

  readonly configJson = input('{}');
  readonly outgoingFlows = input<WorkflowOutgoingFlowOption[]>([]);
  readonly configJsonChange = output<string>();
  readonly flowConditionApply = output<{ flowId: string; conditionExpression: string }>();

  catalog: WorkflowActionCatalogApiItem[] = [];
  rows: ActionRoutingRow[] = [];

  ngOnInit(): void {
    this.api.getActionsCatalog().subscribe({
      next: items => {
        this.catalog = items;
        this.syncRows();
      },
      error: () => {
        this.catalog = [];
        this.syncRows();
      }
    });
    this.syncRows();
  }

  ngOnChanges(): void {
    this.syncRows();
  }

  flowOptions() {
    return this.outgoingFlows().map(flow => ({
      label: flow.name || flow.bpmnElementId || flow.id,
      value: flow.id
    }));
  }

  actionLabel(actionKey: string): string {
    const item = this.catalog.find(entry => entry.actionKey === actionKey);
    if (item?.labelKey) {
      const translated = this.t(item.labelKey);
      if (translated !== item.labelKey) {
        return translated;
      }
    }
    return actionKey;
  }

  onBindingChanged(row: ActionRoutingRow): void {
    this.emitConfig();
    if (row.flowId && row.suggestedConditionExpression) {
      this.flowConditionApply.emit({
        flowId: row.flowId,
        conditionExpression: row.suggestedConditionExpression
      });
    }
  }

  applyCondition(row: ActionRoutingRow): void {
    if (!row.flowId || !row.suggestedConditionExpression) return;
    this.flowConditionApply.emit({
      flowId: row.flowId,
      conditionExpression: row.suggestedConditionExpression
    });
  }

  private syncRows(): void {
    const config = parseConfigJson<WorkflowInteractiveOperatorActionConfig>(this.configJson(), {});
    const bindings = config.outgoingFlowBindings ?? [];
    const bindingLookup = new Map(bindings.map(item => [item.actionKey, item.flowId]));

    this.rows = this.catalog.map(item => ({
      actionKey: item.actionKey,
      variableKey: item.variableKey,
      variableValue: item.variableValue,
      suggestedConditionExpression: item.suggestedConditionExpression,
      flowId: bindingLookup.get(item.actionKey) ?? ''
    }));
  }

  private emitConfig(): void {
    const outgoingFlowBindings: WorkflowOutgoingFlowBinding[] = this.rows
      .filter(row => row.flowId)
      .map(row => ({ actionKey: row.actionKey, flowId: row.flowId }));

    const config = parseConfigJson<WorkflowInteractiveOperatorActionConfig>(this.configJson(), {});
    const merged: WorkflowInteractiveOperatorActionConfig = {
      ...config,
      actions: this.catalog.map(item => ({
        actionKey: item.actionKey,
        variableKey: item.variableKey,
        variableValue: item.variableValue,
        labelKey: item.labelKey
      })),
      outgoingFlowBindings
    };

    this.configJsonChange.emit(stringifyConfigJson(merged));
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}

interface ActionRoutingRow {
  actionKey: string;
  variableKey: string;
  variableValue: string;
  suggestedConditionExpression: string;
  flowId: string;
}
